import { useContext } from 'react';

import {
  Button,
  Icons,
  DialogFooter,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import {
  DialogHeading,
  FeaturesScriptSplitDialogContent,
  StepContainer,
} from 'hooks/dialogs/cluster/_components/new-onboarding';

import { CastctlConnectInstructionsContext } from './CastctlConnectInstructionsContext';
import { FeaturesSection } from './FeaturesSection';
import { CastctlFeatureSelection } from './types';

type Props = {
  featureSelection: CastctlFeatureSelection;
  onFeatureChange: (params: {
    feature: keyof CastctlFeatureSelection;
    enabled: boolean;
  }) => void;
  onDone: () => void;
  onClose: () => void;
  onSwitchToLegacyScript?: () => void;
};

export const SetupStep = ({
  featureSelection,
  onFeatureChange,
  onDone,
  onClose,
  onSwitchToLegacyScript,
}: Props) => {
  // The right-hand "Run in your terminal" area is pluggable via the
  // CastctlConnectInstructionsContext. Main console (no provider) renders
  // the original InstructionsSection; OneClick wraps the app root with a
  // provider that supplies the multi-method tabs component.
  const ConnectInstructions = useContext(CastctlConnectInstructionsContext);

  return (
    <StepContainer data-testid="castctl-main-step">
      <DialogHeading
        onClose={onClose}
        sx={{ borderBottom: tokens.theme.border.structural.neutral }}
        hideTerraformLink
      >
        Connect your Kubernetes cluster
      </DialogHeading>
      <FeaturesScriptSplitDialogContent
        leftTitle="Choose features"
        left={
          <FeaturesSection
            featureSelection={featureSelection}
            onFeatureChange={onFeatureChange}
          />
        }
        rightTitle="Run in your terminal"
        right={<ConnectInstructions featureSelection={featureSelection} />}
      />
      <DialogFooter
        start={
          onSwitchToLegacyScript ? (
            <Stack direction="row" alignItems="center" gap={4}>
              <Icons.Info />
              <Typography variant="P10R">
                castctl is the recommended way to connect. Prefer Helm,
                Terraform, or a manual script?
              </Typography>
              <Button
                variant="text"
                size="sm"
                onClick={onSwitchToLegacyScript}
                testId="switch-to-legacy-button"
              >
                Switch method
              </Button>
            </Stack>
          ) : null
        }
        end={
          <Button onClick={onDone} testId="castctl-done-button">
            Done
          </Button>
        }
      />
    </StepContainer>
  );
};
